import _ from "lodash";
import React from "react";

// components
import PaginatedDropdownOverlay from "UIComponents/PaginatedDropdownOverlay";

// design system
import {
  Tooltip,
  Dropdown,
  Button,
  IconButton,
  FormInputError,
} from "@toddle-edu/ds-web";
import { DeleteOutlined } from "@toddle-edu/ds-icons";

// third party
import classNames from "classnames";

// scss
import classes from "./UnitSelectionDropdown.scss";

const styles = {
  dropdownButtonStyle: { width: "100%" },
  editContainerStyle: { width: "100%" },
  overlayStyle: { width: "100%" },
  imageContainerStyle: {
    width: "100%",
    height: "100%",
    backgroundSize: "cover",
    backgroundPosition: "center",
    backgroundRepeat: "no-repeat",
    borderRadius: "4px",
  },
};

const UnitSelectionDropdown = props => {
  const {
    t,
    mode,
    isUnitPlanFieldDisabled,
    getPopupContainer,
    testId,
    buttonStyle,
    unitSearchText,
    isLoading,
    isLoadingMoreUnitPlans,
    hasNextPage,
    loadMoreUnitPlanFeed,
    networkStatus,
    handleVisibleChange,
    selectedUnitPlan,
    handleClearSelection,
    handleSearchTextChange,
    unitPlanOptions,
    handleUnitSelection,
    isVisible,
    placeholder,
    error,
  } = props;

  const renderSelectedUnitImage = () => {
    const { backgroundImage } = selectedUnitPlan;

    if (!backgroundImage) {
      return null;
    }

    return (
      <div className={classes.imageContainer}>
        <div
          style={{
            backgroundImage: backgroundImage,
            ...styles.imageContainerStyle,
          }}
        ></div>
      </div>
    );
  };

  const renderDropdownButtonContentComponent = () => {
    const selectPlaceholder = !_.isEmpty(placeholder)
      ? placeholder
      : t("common:select");

    const label = _.isEmpty(selectedUnitPlan)
      ? selectPlaceholder
      : selectedUnitPlan?.label;

    const buttonLabelClasses = classNames({
      [classes.buttonLabel]: true,
      [classes.disabledButtonLabel]: isUnitPlanFieldDisabled,
      [classes.buttonPlaceholderLabel]: _.isEmpty(selectedUnitPlan),
    });

    return (
      <div className={classes.buttonContainer}>
        {renderSelectedUnitImage()}
        <div className={buttonLabelClasses}>{label}</div>
      </div>
    );
  };

  const renderOverlay = () => {
    const searchPlaceholder = `${t("common:search_label", {
      label: t("common:unit"),
    })}..`;

    return (
      <PaginatedDropdownOverlay
        isSearchEnabled={true}
        searchText={unitSearchText}
        searchPlaceholder={searchPlaceholder}
        onChangeSearchText={handleSearchTextChange}
        options={unitPlanOptions}
        isLoadingFeed={isLoading}
        isLoadingMoreFeed={isLoadingMoreUnitPlans}
        hasNextPage={hasNextPage}
        loadMoreFeed={loadMoreUnitPlanFeed}
        networkStatus={networkStatus}
        onSelectItem={handleUnitSelection}
        imageContainerStyle={styles.imageContainerStyle}
        emptyStateText={t("unitPlan:no_units_created")}
        searchEmptyStateText={t("unitPlan:no_all_units_found")}
        onClose={() => handleVisibleChange(false)}
      />
    );
  };

  const renderDropdownButtonRightIcon = () => {
    if (!_.isEmpty(selectedUnitPlan) && !isUnitPlanFieldDisabled) {
      return (
        <div>
          <div className={classes.deleteIcon}>
            <IconButton
              icon={<DeleteOutlined variant={"subtle"} />}
              onClick={handleClearSelection}
              _legacy_variant="subtle"
              variant="neutral"
              _legacy_size="x-small"
              size="small"
            />
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <div
      className={classes.unitDropdownContainer}
      style={styles.editContainerStyle}
      onClick={e => e?.stopPropagation()}
      onKeyDown={e => e.key === "Tab" && e.stopPropagation()}
      role={"button"}
      tabIndex={0}
      aria-label="unit selection dropdown"
    >
      <Tooltip
        tooltip={t("classRoom:cannot_edit_unit_for_this_task")}
        trigger={
          isUnitPlanFieldDisabled && mode === "edit" ? ["hover"] : [null]
        }
        showArrow={true}
        placement={"bottomLeft"}
      >
        <Dropdown
          visible={isVisible}
          overlay={renderOverlay()}
          overlayStyle={!getPopupContainer ? styles.overlayStyle : {}}
          onVisibleChange={handleVisibleChange}
          disabled={isUnitPlanFieldDisabled}
          getPopupContainer={getPopupContainer}
          testId={testId}
        >
          <Button
            _legacy_variant="plain"
            variant="neutral"
            type="plain"
            size={"medium"}
            style={{ ...styles.dropdownButtonStyle, ...buttonStyle }}
            rightIcon={renderDropdownButtonRightIcon()}
            disabled={mode === "view"}
            testId={testId}
          >
            {renderDropdownButtonContentComponent()}
          </Button>
        </Dropdown>
      </Tooltip>
      {!!error && <FormInputError error={error} dsVersion="1.0" />}
    </div>
  );
};

export default UnitSelectionDropdown;
